import React, { Suspense } from 'react';
import { ArrowDown } from 'lucide-react';
import type {
  Message,
  MessageReaction,
  ToolCall,
  SupportRecord,
  ProductRecord,
  BubbleOption,
  ProductAction,
  ProductCard,
} from '../types';
import MessageBubble from './messages/MessageBubble';
import ActiveToolCalls from './messages/ActiveToolCalls';
import StreamingMessage from './messages/StreamingMessage';
import TypingIndicator from './messages/TypingIndicator';
import ErrorBoundary from './ErrorBoundary';
import { isSafeUrl } from '../utils/urlSafety';
import ButtonsMessage from './rich/ButtonsMessage';
import { preloadRichMessages } from './rich/lazyRich';
import { useChatConfig } from '../context/ChatContext';
import { normalizeActionValue } from '../utils/actionFilters';
import type { WidgetStrings, WidgetStringKey } from '../utils/i18n';
import { DEFAULT_WIDGET_STRINGS, formatCount } from '../utils/i18n';
import { useStreamingAnnouncements } from '../hooks/useStreamingAnnouncements';
import { useMessageWindow, DEFAULT_EXPAND_BY } from '../hooks/useMessageWindow';

const DEFAULT_ACTION_LABELS: Record<string, WidgetStringKey> = {
  'Order Status': 'actionOrderStatus',
  'Product Question': 'actionProductQuestion',
  'Return / Exchange': 'actionReturnExchange',
};

const RecordView = React.lazy(() => import('./records/RecordView'));
const STATESET_AI_TERMS_URL =
  'https://stateset.com/policies/other-policies/business-chats-using-ai-from-stateset';

interface ResolvedBubbleOption {
  label: string;
  actionValue: string;
  href?: string;
  target?: '_self' | '_blank';
  isReturnExchange: boolean;
}

function resolveBubbleOption(option: BubbleOption): ResolvedBubbleOption {
  const isString = typeof option === 'string';
  const label = isString ? option : option.label;
  const value = isString ? option : option.value || option.label;
  const href = !isString ? option.href : undefined;
  const target = !isString ? option.target : undefined;
  const normalizedLabel = normalizeActionValue(label);
  const normalizedValue = normalizeActionValue(String(value || ''));
  const isReturnExchange =
    normalizedLabel === 'return exchange' ||
    normalizedLabel === 'return or exchange' ||
    normalizedValue === 'return exchange' ||
    normalizedValue === 'return or exchange';

  return {
    label,
    actionValue: isReturnExchange
      ? '__return_exchange__'
      : typeof value === 'string'
        ? value.trim()
        : String(value || '').trim(),
    href,
    target,
    isReturnExchange,
  };
}

interface MainContentProps {
  view: string;
  viewMode?: string;
  onModeChange: (viewKey: string, modeKey: string) => void;
  records: Record<string, unknown[]>;
  messages: Message[];
  isTyping: boolean;
  isMobile?: boolean;
  error: string;
  messagesEndRef: React.RefObject<HTMLDivElement | null>;
  messagesContainerRef?: React.RefObject<HTMLDivElement | null>;
  onMessagesScroll?: (event: React.UIEvent<HTMLDivElement>) => void;
  streamingMessage: Message | null;
  activeToolCalls: ToolCall[];
  onButtonClick?: (value: string) => void;
  onProductAction?: (payload: { action: ProductAction; product: ProductCard }) => void;
  onOrderAction?: (payload: {
    action: { label: string; value?: string; href?: string; target?: '_self' | '_blank' };
    orderId?: string | null;
    orderName?: string | null;
  }) => void;
  onFormSubmit?: (formId: string | undefined, data: Record<string, string>) => void;
  onRetryMessage?: (messageId: string) => void;
  retryDisabled?: boolean;
  onReaction?: (messageId: string, reaction: MessageReaction, comment?: string) => void;
  emptyStateMessage?: string;
  bubbleOptions?: BubbleOption[];
  customerEmailCapture?: React.ReactNode;
  suggestedActions?: string[];
  /** Only the built-in fallback actions use translated labels; values stay stable. */
  localizeActions?: boolean;
  onSuggestedAction?: (value: string) => void;
  panelId: string;
  labelledBy: string;
  showScrollToBottom?: boolean;
  newMessagesCount?: number;
  onScrollToBottom?: () => void;
  strings?: WidgetStrings;
  direction?: 'ltr' | 'rtl';
  /** Speak start/progress/completion cues for streamed replies (default true). */
  announceStreaming?: boolean;
  /** Render only the last N messages (`0` = all); older ones sit behind "Show earlier". */
  maxRenderedMessages?: number;
  /** Whether the log is scrolled to the bottom (from `useScrollManagement`). */
  atBottom?: boolean;
  /** Fetch the rich-message chunks on mount (default true; the idle warm-up render skips it). */
  preloadRich?: boolean;
}

const MainContent = React.memo(function MainContent({
  view,
  viewMode,
  onModeChange,
  records,
  messages,
  isTyping,
  isMobile = false,
  error,
  messagesEndRef,
  messagesContainerRef,
  onMessagesScroll,
  streamingMessage,
  activeToolCalls,
  onButtonClick,
  onProductAction,
  onOrderAction,
  onFormSubmit,
  onRetryMessage,
  retryDisabled = false,
  onReaction,
  emptyStateMessage,
  bubbleOptions,
  customerEmailCapture,
  suggestedActions,
  localizeActions = false,
  onSuggestedAction,
  panelId,
  labelledBy,
  showScrollToBottom,
  newMessagesCount,
  onScrollToBottom,
  strings = DEFAULT_WIDGET_STRINGS,
  direction = 'ltr',
  announceStreaming = true,
  maxRenderedMessages,
  atBottom = true,
  preloadRich = true,
}: MainContentProps) {
  const { trackAnalytics } = useChatConfig();

  // The panel is open: fetch the rich-message renderers now so a reply that needs
  // product/order cards, a form or citations renders without a loading frame.
  React.useEffect(() => {
    if (preloadRich) void preloadRichMessages();
  }, [preloadRich]);

  const { announcement, suppressTypingCue } = useStreamingAnnouncements({
    isStreaming: !!streamingMessage,
    streamedText: streamingMessage?.text ?? '',
    isTyping,
    streamingMessageId: streamingMessage?.id ?? null,
    messages,
    strings,
    enabled: announceStreaming,
  });

  // The finalised bubble reuses the streamed message's id. Remembering it lets the
  // render below mark that bubble's wrapper aria-live="off" so the log (which
  // announces additions) stays quiet: the status region's completion cue is the
  // single announcement for a streamed reply.
  const [lastStreamedId, setLastStreamedId] = React.useState<string | null>(null);
  const streamingId = streamingMessage?.id ?? null;
  React.useEffect(() => {
    if (streamingId) setLastStreamedId(streamingId);
  }, [streamingId]);

  // Keep retries rendered while failed or in flight; stable ids must be released on completion.
  const [retryingIds, setRetryingIds] = React.useState<string[]>([]);
  React.useEffect(() => {
    setRetryingIds(prev => {
      if (prev.length === 0) return prev;
      const next = prev.filter(id =>
        messages.some(m => m.id === id && (m.status === 'failed' || retryDisabled))
      );
      return next.length === prev.length ? prev : next;
    });
  }, [messages, retryDisabled]);
  const handleRetryMessage = React.useMemo(
    () =>
      onRetryMessage
        ? (messageId: string) => {
            setRetryingIds(prev => (prev.includes(messageId) ? prev : [...prev, messageId]));
            onRetryMessage(messageId);
          }
        : undefined,
    [onRetryMessage]
  );

  const {
    visible: visibleMessages,
    hiddenCount,
    showMore,
  } = useMessageWindow(messages, {
    maxRendered: maxRenderedMessages,
    expandBy: DEFAULT_EXPAND_BY,
    streamingId,
    retryingIds,
    atBottom,
  });

  const logRef = React.useRef<HTMLDivElement | null>(null);
  const setLogRef = React.useCallback(
    (node: HTMLDivElement | null) => {
      logRef.current = node;
      if (messagesContainerRef) {
        (messagesContainerRef as React.MutableRefObject<HTMLDivElement | null>).current = node;
      }
    },
    [messagesContainerRef]
  );
  // Scroll anchoring for prepended history: remember the height before the
  // reveal and shift scrollTop by the growth, so the visitor's view stays put.
  const revealRef = React.useRef<{
    scrollHeight: number;
    scrollTop: number;
    focusLog: boolean;
  } | null>(null);
  const handleShowEarlier = React.useCallback(() => {
    const log = logRef.current;
    const button = typeof document !== 'undefined' ? document.activeElement : null;
    revealRef.current = {
      scrollHeight: log?.scrollHeight ?? 0,
      scrollTop: log?.scrollTop ?? 0,
      focusLog:
        hiddenCount <= DEFAULT_EXPAND_BY &&
        !!button &&
        (button as HTMLElement).dataset?.rwShowEarlier === 'true',
    };
    showMore();
  }, [hiddenCount, showMore]);
  React.useLayoutEffect(() => {
    const pending = revealRef.current;
    const log = logRef.current;
    if (!pending || !log) return;
    revealRef.current = null;
    log.scrollTop = pending.scrollTop + (log.scrollHeight - pending.scrollHeight);
    // The button is gone once the last batch is revealed; keep focus in the log.
    if (pending.focusLog && hiddenCount === 0) log.focus();
  }, [visibleMessages, hiddenCount]);

  // One polite, atomic status region outside the log so its cues do not collide
  // with the log's aria-relevant="additions".
  const streamStatusRegion = (
    <div
      className="sr-only"
      role="status"
      aria-live="polite"
      aria-atomic="true"
      data-rw-stream-status="true"
    >
      {announcement}
    </div>
  );

  const handleBubbleOptionClick = React.useCallback(
    (
      label: string,
      actionValue: string,
      href: string | undefined,
      target: '_self' | '_blank' | undefined,
      source: 'main_empty' | 'main_post_intro',
      isReturnExchange: boolean
    ) => {
      const safeHref = href && isSafeUrl(href) ? href : '';
      trackAnalytics('bubble_option_click', {
        label,
        value: actionValue,
        source,
        hasHref: Boolean(safeHref),
        target: target || null,
      });

      // Return/exchange is an internal guided flow and should not navigate away,
      // even if a bubble definition also includes an href.
      if (!isReturnExchange && safeHref && typeof window !== 'undefined') {
        if (target === '_self') {
          try {
            window.open(safeHref, '_self');
          } catch {
            try {
              window.location.assign(safeHref);
            } catch {
              // Ignore if navigation is blocked.
            }
          }
        } else {
          window.open(safeHref, '_blank', 'noopener,noreferrer');
        }
        return;
      }

      if (isReturnExchange) onSuggestedAction?.('__return_exchange__');
      else if (actionValue) onSuggestedAction?.(actionValue);
    },
    [onSuggestedAction, trackAnalytics]
  );

  const renderBubbleOptions = React.useCallback(
    (
      options: BubbleOption[],
      source: 'main_empty' | 'main_post_intro',
      buttonClassName: string,
      keyPrefix: string
    ) =>
      options.map((option, index) => {
        const { label, actionValue, href, target, isReturnExchange } = resolveBubbleOption(option);
        const key = `${keyPrefix}-${label}-${index}`;
        return (
          <button
            key={key}
            type="button"
            onClick={() =>
              handleBubbleOptionClick(label, actionValue, href, target, source, isReturnExchange)
            }
            className={buttonClassName}
          >
            {label}
          </button>
        );
      }),
    [handleBubbleOptionClick]
  );

  if (view !== 'chat') {
    return (
      <div
        id={panelId}
        role="tabpanel"
        aria-labelledby={labelledBy}
        tabIndex={0}
        className="flex-1 flex flex-col overflow-hidden min-h-0 focus:outline-2 focus:outline-blue-500 relative"
      >
        {streamStatusRegion}
        <ErrorBoundary strings={strings}>
          <Suspense
            fallback={
              <div className="flex-1 flex items-center justify-center text-sm text-gray-600">
                {strings.loading}
              </div>
            }
          >
            <RecordView
              type={view}
              records={(records?.[view] as (SupportRecord | ProductRecord)[]) || []}
              mode={viewMode}
              onModeChange={onModeChange}
              strings={strings}
            />
          </Suspense>
        </ErrorBoundary>
      </div>
    );
  }

  return (
    <div
      id={panelId}
      role="tabpanel"
      aria-labelledby={labelledBy}
      tabIndex={0}
      className="flex-1 flex flex-col overflow-hidden min-h-0 focus:outline-2 focus:outline-blue-500 relative"
    >
      {streamStatusRegion}
      <div
        className="flex-1 overflow-y-auto overflow-x-hidden px-4 py-3 space-y-2 min-h-0 chat-scroll rw-chat-surface"
        role="log"
        aria-live="polite"
        aria-relevant="additions"
        aria-label={strings.chatMessages}
        aria-busy={isTyping || !!streamingMessage}
        ref={setLogRef}
        tabIndex={-1}
        onScroll={onMessagesScroll}
      >
        {hiddenCount > 0 && (
          <div className="flex justify-center">
            <button
              type="button"
              data-rw-show-earlier="true"
              onClick={handleShowEarlier}
              className="rw-show-earlier px-3 py-1 rounded-full border border-gray-200 bg-white text-gray-700 text-xs font-medium hover:bg-gray-100 focus:outline-2 focus:outline-blue-500"
            >
              {formatCount(strings, 'showEarlierMessages', hiddenCount)}
            </button>
          </div>
        )}
        {messages.length === 0 && !streamingMessage && (
          <div className="h-full flex flex-col items-center justify-center text-center text-sm text-gray-500">
            {emptyStateMessage || strings.emptyState}
            {bubbleOptions && bubbleOptions.length > 0 ? (
              <div className="rw-bubble-options rw-bubble-options-empty mt-4 flex flex-wrap justify-center gap-2">
                {renderBubbleOptions(
                  bubbleOptions,
                  'main_empty',
                  'px-4 py-2 rounded-full border border-gray-200 bg-white text-gray-700 text-sm font-medium shadow-sm hover:bg-gray-100 focus:outline-2 focus:outline-blue-500',
                  'bubble-empty'
                )}
              </div>
            ) : null}
            {!bubbleOptions || bubbleOptions.length === 0
              ? suggestedActions &&
                suggestedActions.length > 0 && (
                  <div className="mt-3 flex justify-center">
                    <ButtonsMessage
                      content={{
                        buttons: suggestedActions.map(action => ({
                          label:
                            (localizeActions && strings[DEFAULT_ACTION_LABELS[action]]) || action,
                          value: action,
                        })),
                      }}
                      onButtonClick={onSuggestedAction}
                      trackAnalytics={trackAnalytics}
                    />
                  </div>
                )
              : null}
            <p className="mt-4 max-w-xs text-xs leading-5 text-gray-600">
              {strings.aiTermsNotice}{' '}
              <a
                href={STATESET_AI_TERMS_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="font-medium text-gray-700 underline underline-offset-2 hover:opacity-80 focus:outline-2 focus:outline-blue-500"
              >
                {strings.aiTermsLearnMore}
              </a>
            </p>
            {customerEmailCapture}
          </div>
        )}
        {visibleMessages.map(m => (
          <div key={m.id} aria-live={m.id === lastStreamedId ? 'off' : undefined}>
            <MessageBubble
              message={m}
              isMobile={isMobile}
              onButtonClick={onButtonClick}
              onProductAction={onProductAction}
              onOrderAction={onOrderAction}
              onFormSubmit={onFormSubmit}
              onRetryMessage={handleRetryMessage}
              retryDisabled={retryDisabled}
              onReaction={onReaction}
              strings={strings}
              direction={direction}
            />
          </div>
        ))}
        {messages.length > 0 &&
          !messages.some(m => m.sender === 'user') &&
          !messages.some(m => m.richContent?.buttons && m.richContent.buttons.length > 0) &&
          !messages.some(m => typeof m.id === 'string' && m.id.startsWith('intro-')) &&
          bubbleOptions &&
          bubbleOptions.length > 0 && (
            <div className="mt-2 mb-3 px-2">
              <div className="flex justify-center">{customerEmailCapture}</div>
              <div className="rw-bubble-options rw-bubble-options-post mt-2 flex flex-wrap justify-center gap-2">
                {renderBubbleOptions(
                  bubbleOptions,
                  'main_post_intro',
                  'px-4 py-2 rounded-full border border-gray-300 bg-white text-gray-800 text-sm font-medium shadow-sm hover:bg-gray-100 focus:outline-2 focus:outline-blue-500',
                  'bubble-post'
                )}
              </div>
            </div>
          )}
        <ActiveToolCalls toolCalls={activeToolCalls} strings={strings} />
        {streamingMessage && <StreamingMessage message={streamingMessage} />}
        {isTyping && !streamingMessage && (
          <TypingIndicator strings={strings} announce={!suppressTypingCue} />
        )}
        {error && (
          <div className="text-xs text-red-600" role="alert">
            {error}
          </div>
        )}
        <div aria-hidden="true" id="messages-end" ref={messagesEndRef} />
      </div>
      {showScrollToBottom && (
        <button
          type="button"
          onClick={() => onScrollToBottom?.()}
          className={`rw-scroll-to-bottom absolute bottom-4 ${direction === 'rtl' ? 'left-4' : 'right-4'} flex items-center gap-2 px-3 py-2 rounded-full border border-gray-200 bg-white text-gray-700 shadow hover:bg-gray-100 focus:outline-2 focus:outline-blue-500`}
          aria-label={strings.scrollToLatest}
        >
          <ArrowDown className="w-4 h-4" aria-hidden="true" />
          <span className="text-xs font-semibold">{strings.latest}</span>
          {typeof newMessagesCount === 'number' && newMessagesCount > 0 && (
            <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-blue-600 text-white text-xs font-semibold">
              {newMessagesCount > 99 ? '99+' : String(newMessagesCount)}
            </span>
          )}
        </button>
      )}
    </div>
  );
});

export default MainContent;
